import { useQuery } from '@tanstack/react-query';
import { Card, Table, Tag, Typography, EmptyState } from '../../ui';
import { request } from '../../api/client';
import { formatIst } from '../../lib/time';
import { fmtQty } from '../../lib/format';

interface LowAlert {
  id: string;
  productId: string;
  sku: string | null;
  productName: string;
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  available: string;
  minLevel: string;
  openedAt: string | null;
  lastNotifiedAt: string | null;
}

/**
 * The durable "currently below minimum" list — warehouse-scoped server-side (a Warehouse Manager sees
 * only their warehouse(s); admins see all). This is the source of truth a newly-arrived WM/Admin can
 * pull regardless of who received past push notifications. Rows clear automatically when restocked.
 */
export function LowStockAlertsPage({ embedded }: { embedded?: boolean } = {}) {
  const { data, isLoading } = useQuery({
    queryKey: ['low-stock-alerts'],
    queryFn: () => request<LowAlert[]>({ url: '/stock/alerts/low-stock' }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const rows = data ?? [];

  return (
    <div>
      {!embedded && (
        <>
          <Typography.Title level={4} style={{ marginBottom: 2 }}>Low Stock Alerts</Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
            Products currently at or below their minimum stock level. Resolves automatically when restocked.
          </Typography.Paragraph>
        </>
      )}
      <Card>
        <Table<LowAlert>
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} open` }}
          locale={{ emptyText: <EmptyState title="No low-stock alerts — all good." /> }}
          columns={[
            {
              title: 'Product', dataIndex: 'productName',
              render: (v: string, r) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{v}</div>
                  <div style={{ fontSize: 12, color: '#8c8c8c' }}>{r.sku}</div>
                </div>
              ),
            },
            { title: 'Warehouse', dataIndex: 'warehouseCode', width: 150, render: (v: string) => <Tag tone="blue">{v}</Tag> },
            { title: 'On hand', dataIndex: 'available', align: 'right' as const, width: 120, render: (v: any) => <b style={{ color: '#cf1322' }}>{fmtQty(Number(v))}</b> },
            { title: 'Minimum', dataIndex: 'minLevel', align: 'right' as const, width: 110, render: (v: any) => fmtQty(Number(v)) },
            {
              title: 'Short by', align: 'right' as const, width: 110,
              render: (_: any, r) => { const s = Number(r.minLevel) - Number(r.available); return <Tag tone="red" style={{ margin: 0 }}>{fmtQty(s > 0 ? s : 0)}</Tag>; },
            },
            { title: 'Since', dataIndex: 'openedAt', width: 180, render: (v: string | null) => (v ? formatIst(v) : '—') },
          ]}
        />
      </Card>
    </div>
  );
}
